import type { WalkStage } from '../research-room/room-arrival';
import type { PageStatus } from './use-browser-session';

/**
 * The walk, told on the board it ends on.
 *
 * `/browse` used to be three screens — a boot page, a browser under a status
 * strip, then the board — and the jump from the second to the third was the
 * moment the product changed shape under the user. Now the browser *is* a
 * card on the board, where the capture will land, and the research room is
 * already open beside it with Floto narrating (research-room/room-arrival). When the walk is done the live card
 * becomes the stitched page in place, and the board it hands over to is laid
 * out identically.
 */

/**
 * The card's contents, in the worker's own pixels — the board scales it.
 *
 * Not `ViewportStage`: that measures itself and forwards input, and inside the
 * board's transformed layer both would be wrong. The walk is driving, so there
 * is no input to forward, and the board already knows the scale.
 */
export function LiveScreen({
  developed,
  failed,
  flash,
  frame,
  host,
  pageStatus,
  stage,
  viewportHeight,
}: {
  /** The stitched page, once there is one. */
  developed: string | null;
  failed: boolean;
  flash: boolean;
  frame: string | null;
  host: string;
  pageStatus: PageStatus;
  stage: WalkStage;
  viewportHeight: number;
}) {
  const reading =
    !failed && pageStatus === 'ready' && (stage === 'load' || stage === 'walk');

  return (
    <div className="relative size-full overflow-hidden rounded-thumb bg-surface shadow-floating ring-1 ring-hairline-alt">
      {frame ? (
        <img
          alt={`Live view of ${host}`}
          className="block w-full object-cover object-top select-none"
          draggable={false}
          src={frame}
          style={{ height: viewportHeight }}
        />
      ) : (
        <BootSkeleton />
      )}

      {/* The page, stitched: laid over the live frame and revealed downward
          as the card grows to its height. */}
      {developed ? (
        <img
          alt={`The whole of ${host}, stitched`}
          className="absolute inset-0 block size-full object-cover object-top duration-500 animate-in fade-in select-none motion-reduce:animate-none"
          draggable={false}
          src={developed}
        />
      ) : null}

      {/* Still going up to the session: a slow sweep of light, so the page
          reads as on its way rather than done. */}
      {developed && stage === 'save' ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-surface/40 to-transparent motion-reduce:hidden"
        />
      ) : null}

      {pageStatus === 'loading' && !developed ? (
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 h-1.5 overflow-hidden bg-hairline"
        >
          <div className="h-full w-1/3 animate-shimmer bg-ink motion-reduce:w-full" />
        </div>
      ) : null}

      {/* Reading down the page, a screen at a time. */}
      {reading ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 animate-scan motion-reduce:hidden"
          style={{ height: viewportHeight }}
        >
          <div className="h-1 bg-brand/60" />
        </div>
      ) : null}

      {stage === 'stitch' && !developed ? (
        <div
          aria-hidden="true"
          className="absolute inset-0 animate-pulse bg-warm/50 motion-reduce:animate-none"
        />
      ) : null}

      {/* Shutter: a white wash that fades out, plus a lime ring marking the
          exact frame that was taken. */}
      {flash && !developed ? (
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0"
          style={{ height: viewportHeight }}
        >
          <div className="absolute inset-0 animate-capture-flash bg-white motion-reduce:hidden" />
          <div className="absolute inset-0 animate-capture-flash ring-8 ring-highlight ring-inset" />
        </div>
      ) : null}

      {failed ? (
        <div
          aria-hidden="true"
          className="absolute inset-0 bg-warm-alt/80 duration-200 animate-in fade-in"
        />
      ) : null}
    </div>
  );
}

/** A browser page assembling itself, drawn at the worker's scale. */
function BootSkeleton() {
  return (
    <div aria-hidden="true" className="relative size-full bg-warm-alt">
      <div className="animate-pulse space-y-10 p-16 motion-reduce:animate-none">
        <div className="flex items-center justify-between">
          <div className="h-8 w-40 rounded-pill bg-hairline-alt" />
          <div className="flex gap-6">
            <div className="h-5 w-24 rounded-pill bg-hairline" />
            <div className="h-5 w-24 rounded-pill bg-hairline" />
            <div className="h-5 w-24 rounded-pill bg-hairline" />
          </div>
        </div>
        <div className="space-y-5 pt-16">
          <div className="h-6 w-1/4 rounded-pill bg-hairline-alt" />
          <div className="h-16 w-3/4 rounded-pill bg-hairline" />
          <div className="h-16 w-1/2 rounded-pill bg-hairline" />
        </div>
        <div className="flex gap-8 pt-8">
          <div className="h-40 flex-1 rounded-card bg-hairline" />
          <div className="h-40 flex-1 rounded-card bg-hairline" />
          <div className="h-40 flex-1 rounded-card bg-hairline" />
        </div>
      </div>
      <div className="pointer-events-none absolute inset-0 animate-shimmer bg-gradient-to-r from-transparent via-highlight/25 to-transparent motion-reduce:hidden" />
    </div>
  );
}
